import cx from 'classnames';
import {map, take} from 'lodash';
import {Fragment, useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, CreditCardIcon, Divider, InformationModal} from 'components';
import {UserContext} from 'context';
import {TransactionStatus} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {BillingPlatformTransaction, CreditCard} from 'types';
import {dateUtils} from 'utils';
import {formatDate, transformUtils} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.BILLING.TRANSACTIONS;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type Props = {
    transactions: BillingPlatformTransaction[];
}

const INITIAL_ITEM_COUNT = 3;

export const TransactionsGrid: React.FC<Props> = ({transactions}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);

    const hasViewAllBtn = useMemo(() => transactions?.length > INITIAL_ITEM_COUNT, [transactions]);
    const initialTransactions = useMemo(() => take(transactions, INITIAL_ITEM_COUNT), [transactions]);
    const formatPaymentDate = useCallback((date: Date) => {
        const dateTimeFormat = dateUtils.getDateTimeFormat(dateFormat, timeFormat);
        return formatDate(date, timeZone, dateTimeFormat);
    }, [timeZone, dateFormat, timeFormat]);
    const [modalOpen, setModalOpen] = useState(false);

    const renderPaymentSource = useCallback((creditCard: Partial<CreditCard>, paymentSourceId: string) => (
        <div className='tw-flex-center'>
            {creditCard?.brand && <CreditCardIcon className='tw-mr-2' iconSize='lg' name={creditCard?.brand} />}
            <div>{creditCard?.last4 || paymentSourceId}</div>
        </div>
    ), []);

    const renderTransactionItem = useCallback((item: BillingPlatformTransaction, index: number, lastIndex: number) => (
        <Fragment key={index}>
            <div className='tw-flex tw-justify-between'>
                <div>{renderPaymentSource(item.paymentDetails?.card, item.paymentSourceId)}</div>
                <div className='tw-font-bold'>{transformUtils.formatPrice(item.amount, item.currency)}</div>
            </div>
            <div className='tw-flex tw-justify-between'>
                <div className='tw-text-default-dimmed'>{formatPaymentDate(item.date)}</div>
                <div className={cx('tw-font-bold', {
                    'tw-text-success': item.status === TransactionStatus.Success,
                    'tw-text-alert': item.status === TransactionStatus.Failure
                })}
                >
                    {item.status}
                </div>
            </div>
            {index !== lastIndex && <Divider className='tw-my-2' />}
        </Fragment>
    ), [formatPaymentDate, renderPaymentSource]);

    const renderTransactions = useCallback((items: BillingPlatformTransaction[]) =>
        map(items, (payment, index) => renderTransactionItem(payment, index, items.length - 1)), [renderTransactionItem]);

    return (
        <div className='sm:tw-hidden'>
            {renderTransactions(initialTransactions)}
            {hasViewAllBtn &&
                <div className='tw-text-right'>
                    <Button className='tw-mt-4' onClick={() => setModalOpen(true)}>{t(T_KEYS_COMMON.BUTTONS.VIEW_ALL)}</Button>
                </div>}
            <InformationModal
                title={t(T_KEYS.VIEW_ALL_MODAL.TITLE)}
                open={modalOpen}
                maxWidth='xs'
                fullWidth
                onClose={() => setModalOpen(false)}
            >
                {renderTransactions(transactions)}
            </InformationModal>
        </div>
    );
};